import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Table2, LayoutGrid, CalendarCheck, Search, RefreshCw, FolderKanban, GanttChartSquare } from 'lucide-react';

import { ProjectTableView } from '@/components/projects/ProjectTableView';
import { ProjectKanbanView } from '@/components/projects/ProjectKanbanView';
import { ProjectTaskDialog } from '@/components/projects/ProjectTaskDialog';
import { TaskUpdateDialog } from '@/components/projects/TaskUpdateDialog';
import { ProjectWeeklyReview } from '@/components/projects/ProjectWeeklyReview';
import { ProjectTimelineView } from '@/components/projects/ProjectTimelineView';
import { STATUS_META, STATUS_ORDER, isoWeekOf, shiftWeek } from '@/constants/projectMeta';
import { usePersistedState } from '@/hooks/usePersistedState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function ProjectManagementPage() {
  const [meta, setMeta] = useState({ departments: [], members: [], can_manage: false, me: {} });
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [projects, setProjects] = useState([]);
  const [allTotal, setAllTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = usePersistedState('projects:view', 'table');
  const [department, setDepartment] = usePersistedState('projects:department', 'all');
  const [status, setStatus] = usePersistedState('projects:status', 'all');
  const [picFilter, setPicFilter] = usePersistedState('projects:pic', 'all');
  const [projectFilter, setProjectFilter] = usePersistedState('projects:project', 'all');
  const [search, setSearch] = useState('');
  const [week, setWeek] = useState(isoWeekOf(new Date()));
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [taskDialog, setTaskDialog] = useState(false);
  const [updateTask, setUpdateTask] = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const loadMeta = useCallback(async () => {
    const res = await axios.get(`${API}/projects/meta`, authHeader());
    setMeta(res.data);
  }, []);

  const loadProjects = useCallback(async () => {
    const res = await axios.get(`${API}/projects/labels`, authHeader());
    setProjects(res.data.projects || []);
    setAllTotal(res.data.total || 0);
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (department !== 'all') params.department = department;
      if (status !== 'all') params.status = status;
      if (picFilter !== 'all') params.pic_id = picFilter;
      if (projectFilter !== 'all') params.project = projectFilter;
      const res = await axios.get(`${API}/projects/tasks`, { ...authHeader(), params });
      setTasks(res.data.tasks || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error('Failed to load project tasks');
    } finally {
      setLoading(false);
    }
  }, [department, status, picFilter, projectFilter]);

  const reviewParams = useCallback((wk) => {
    const params = { week: wk };
    if (department !== 'all') params.department = department;
    if (projectFilter !== 'all') params.project = projectFilter;
    return params;
  }, [department, projectFilter]);

  const loadReview = useCallback(async (wk) => {
    setReviewLoading(true);
    try {
      const res = await axios.get(`${API}/projects/weekly-review`, { ...authHeader(), params: reviewParams(wk) });
      setReview(res.data);
    } catch (e) {
      toast.error('Failed to load weekly review');
    } finally {
      setReviewLoading(false);
    }
  }, [reviewParams]);

  const exportReview = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(`${API}/projects/weekly-review/export`, {
        ...authHeader(), params: { ...reviewParams(week), format }, responseType: 'blob',
      });
      const slug = (projectFilter === 'all' ? 'all-projects' : projectFilter).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly-review-${slug || 'project'}-${week}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(format === 'pdf' ? 'PDF downloaded' : 'PowerPoint downloaded');
    } catch (e) {
      toast.error('Export failed, please try again');
    } finally {
      setExporting('');
    }
  };

  const loadTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const params = {};
      if (department !== 'all') params.department = department;
      const res = await axios.get(`${API}/projects/timeline`, { ...authHeader(), params });
      setTimeline(res.data);
    } catch (e) {
      toast.error('Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [department]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadTasks(); loadProjects(); }, [loadTasks, loadProjects]);
  useEffect(() => { if (view === 'weekly') loadReview(week); }, [view, week, loadReview]);
  useEffect(() => { if (view === 'timeline') loadTimeline(); }, [view, loadTimeline]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => `${t.title} ${t.notes || ''} ${t.pic_name || ''} ${t.project || ''}`.toLowerCase().includes(q));
  }, [tasks, search]);

  const refreshAll = () => {
    loadTasks();
    loadProjects();
    if (view === 'weekly') loadReview(week);
    if (view === 'timeline') loadTimeline();
  };

  const saveTask = async (form) => {
    try {
      if (editTask) {
        await axios.put(`${API}/projects/tasks/${editTask.id}`, form, authHeader());
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/projects/tasks`, form, authHeader());
        toast.success('Task created — the PIC now has it in My Tasks');
      }
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save task');
      throw e;
    }
  };

  const changeStatus = async (task, newStatus) => {
    try {
      await axios.put(`${API}/projects/tasks/${task.id}`, { status: newStatus }, authHeader());
      setTasks((prev) => prev.map((t) => (t.id === task.id
        ? { ...t, status: newStatus, progress: newStatus === 'completed' ? 100 : t.progress } : t)));
      loadProjects();
      if (view === 'weekly') loadReview(week);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change status');
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"? The PIC checklist item will be removed too.`)) return;
    try {
      await axios.delete(`${API}/projects/tasks/${task.id}`, authHeader());
      toast.success('Task deleted');
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete task');
    }
  };

  const submitUpdate = async (task, payload) => {
    try {
      const res = await axios.post(`${API}/projects/tasks/${task.id}/updates`, payload, authHeader());
      const entry = res.data.update;
      setTasks((prev) => prev.map((t) => (t.id === task.id
        ? { ...t, updates: [...(t.updates || []), entry], progress: entry.progress, status: entry.status } : t)));
      setUpdateTask((prev) => (prev && prev.id === task.id
        ? { ...prev, updates: [...(prev.updates || []), entry], progress: entry.progress, status: entry.status } : prev));
      toast.success('Update submitted');
      loadProjects();
      if (view === 'weekly') loadReview(week);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit update');
      throw e;
    }
  };

  const deleteUpdate = async (task, entry) => {
    try {
      await axios.delete(`${API}/projects/tasks/${task.id}/updates/${entry.id}`, authHeader());
      const strip = (t) => ({ ...t, updates: (t.updates || []).filter((u) => u.id !== entry.id) });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? strip(t) : t)));
      setUpdateTask((prev) => (prev && prev.id === task.id ? strip(prev) : prev));
      if (view === 'weekly') loadReview(week);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete update');
    }
  };

  const addComment = async (task, text) => {
    try {
      const res = await axios.post(`${API}/projects/tasks/${task.id}/comments`, { text }, authHeader());
      const entry = res.data.comment;
      const push = (t) => ({ ...t, comments: [...(t.comments || []), entry] });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? push(t) : t)));
      setUpdateTask((prev) => (prev && prev.id === task.id ? push(prev) : prev));
      toast.success('Comment posted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post comment');
    }
  };

  const removeComment = async (task, entry) => {
    try {
      await axios.delete(`${API}/projects/tasks/${task.id}/comments/${entry.id}`, authHeader());
      const strip = (t) => ({ ...t, comments: (t.comments || []).filter((c) => c.id !== entry.id) });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? strip(t) : t)));
      setUpdateTask((prev) => (prev && prev.id === task.id ? strip(prev) : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const applyToTask = (taskId, fn, progress) => {
    const run = (t) => {
      const next = fn(t);
      return progress === undefined || progress === null ? next : { ...next, progress };
    };
    setTasks((prev) => prev.map((t) => (t.id === taskId ? run(t) : t)));
    setUpdateTask((prev) => (prev && prev.id === taskId ? run(prev) : prev));
  };

  const addSubtask = async (task, title) => {
    try {
      const res = await axios.post(`${API}/projects/tasks/${task.id}/subtasks`, { title }, authHeader());
      applyToTask(task.id, (t) => ({ ...t, subtasks: [...(t.subtasks || []), res.data.subtask] }), res.data.progress);
      loadProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add subtask');
    }
  };

  const patchSubtask = async (task, sub, patch) => {
    try {
      const res = await axios.put(`${API}/projects/tasks/${task.id}/subtasks/${sub.id}`, patch, authHeader());
      const { subtask, progress } = res.data;
      applyToTask(task.id, (t) => ({
        ...t, subtasks: (t.subtasks || []).map((s) => (s.id === sub.id ? { ...s, ...subtask } : s)),
      }), progress);
      loadProjects();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update subtask');
      return false;
    }
  };

  const assignSubtask = async (task, sub, memberId) => {
    const member = (meta.members || []).find((m) => m.id === memberId);
    const payload = { assignee_id: member ? member.id : '', assignee_name: member ? member.name : '' };
    if (await patchSubtask(task, sub, payload)) {
      toast.success(member ? `Subtask assigned to ${member.name}` : 'Subtask PIC cleared');
    }
  };

  const removeSubtask = async (task, sub) => {
    try {
      const res = await axios.delete(`${API}/projects/tasks/${task.id}/subtasks/${sub.id}`, authHeader());
      applyToTask(task.id, (t) => ({ ...t, subtasks: (t.subtasks || []).filter((s) => s.id !== sub.id) }), res.data.progress);
      loadProjects();
    } catch (e) {
      toast.error('Failed to delete subtask');
    }
  };

  const openNew = () => { setEditTask(null); setTaskDialog(true); };  const openEdit = (task) => { setEditTask(task); setTaskDialog(true); };

  return (
    <ResponsiveLayout>
      <div className="p-4 lg:p-6" data-testid="project-management-page">
        <SectionHeader
          icon={FolderKanban}
          title="Project Management"
          subtitle="Track projects, deadlines and progress across every department"
          action={
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={refreshAll} data-testid="projects-refresh-btn">
                <RefreshCw size={14} className="mr-1.5" /> Refresh
              </Button>
              {meta.can_manage && (
                <Button size="sm" onClick={openNew} data-testid="projects-new-btn"
                  className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white shadow-lg shadow-[#E63946]/20">
                  <Plus size={16} className="mr-1.5" /> New Task
                </Button>
              )}
            </div>
          }
        />

        {!!projects.length && (
          <div className="flex gap-2.5 overflow-x-auto pb-2 mb-4" data-testid="project-label-cards">
            <button
              onClick={() => setProjectFilter('all')}
              className={`shrink-0 text-left rounded-xl border px-3.5 py-2.5 min-w-[150px] transition-colors ${projectFilter === 'all'
                ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-white/5'}`}
              data-testid="project-card-all"
            >
              <p className="text-xs font-semibold text-[hsl(var(--text-primary))]">All Projects</p>
              <p className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5" data-testid="project-card-all-count">{allTotal} tasks</p>
            </button>
            {projects.map((p) => (
              <button
                key={p.project}
                onClick={() => setProjectFilter(p.project)}
                className={`shrink-0 text-left rounded-xl border px-3.5 py-2.5 min-w-[140px] sm:min-w-[170px] transition-colors ${projectFilter === p.project
                  ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                  : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-white/5'}`}
                data-testid={`project-card-${p.project}`}
              >
                <p className="text-xs font-semibold text-[hsl(var(--text-primary))] truncate">{p.project}</p>
                <p className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">
                  {p.completed}/{p.total} done{p.overdue ? ` · ${p.overdue} overdue` : ''}
                </p>
                <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#E63946] to-[#F97316]" style={{ width: `${p.avg_progress}%` }} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {[
            { id: 'table', label: 'Table', icon: Table2 },
            { id: 'kanban', label: 'Kanban', icon: LayoutGrid },
            { id: 'weekly', label: 'Weekly Review', icon: CalendarCheck },
            { id: 'timeline', label: 'Timeline', icon: GanttChartSquare },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${view === v.id
                ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:bg-white/5'}`}
              data-testid={`projects-view-${v.id}`}
            >
              <v.icon size={13} /> {v.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="projects-filter-bar">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…"
              className="h-9 pl-8 w-44 text-xs" data-testid="projects-search" />
          </div>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="projects-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.project} value={p.project}>{p.project}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="projects-department-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {(meta.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36 text-xs" data-testid="projects-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={picFilter} onValueChange={setPicFilter}>
            <SelectTrigger className="h-9 w-36 text-xs" data-testid="projects-pic-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All PIC</SelectItem>
              {meta.me?.id && <SelectItem value={meta.me.id}>My Tasks</SelectItem>}
              {(meta.members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-[hsl(var(--text-muted))] ml-auto" data-testid="projects-count">
            {visible.length} tasks · {counts.in_progress || 0} in progress · {counts.completed || 0} completed
          </div>
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 lg:p-4">
          {loading && view !== 'weekly' && view !== 'timeline' ? (
            <div className="py-16 text-center text-sm text-[hsl(var(--text-muted))]">Loading…</div>
          ) : view === 'table' ? (
            <ProjectTableView
              tasks={visible}
              canManage={meta.can_manage}
              meId={meta.me?.id}
              today={today}
              onEdit={openEdit}
              onDelete={removeTask}
              onStatusChange={changeStatus}
              onAddUpdate={setUpdateTask}
            />
          ) : view === 'kanban' ? (
            <ProjectKanbanView
              tasks={visible}
              canManage={meta.can_manage}
              meId={meta.me?.id}
              today={today}
              onEdit={openEdit}
              onStatusChange={changeStatus}
              onAddUpdate={setUpdateTask}
            />
          ) : view === 'timeline' ? (
            <ProjectTimelineView
              data={timeline}
              loading={timelineLoading}
              onOpenTask={(taskId) => {
                const t = tasks.find((x) => x.id === taskId);
                if (t) setUpdateTask(t);
              }}
            />
          ) : (
            <ProjectWeeklyReview
              data={review}
              week={week}
              loading={reviewLoading}
              projectLabel={projectFilter === 'all' ? 'All Projects' : projectFilter}
              exporting={exporting}
              onExport={exportReview}
              onWeekChange={(delta) => setWeek((w) => shiftWeek(w, delta))}
              onAddUpdate={(taskId) => {
                const t = tasks.find((x) => x.id === taskId);
                if (t) setUpdateTask(t);
              }}
            />
          )}
        </div>
      </div>

      <ProjectTaskDialog
        open={taskDialog}
        onOpenChange={setTaskDialog}
        task={editTask}
        meta={meta}
        projects={projects}
        canManage={meta.can_manage}
        onSave={saveTask}
      />
      <TaskUpdateDialog
        key={updateTask?.id || 'closed'}
        open={!!updateTask}
        onOpenChange={(o) => !o && setUpdateTask(null)}
        task={updateTask}
        week={week}
        canManage={meta.can_manage}
        meId={meta.me?.id}
        onSubmit={submitUpdate}
        onDeleteUpdate={deleteUpdate}
        onAddComment={addComment}
        onDeleteComment={removeComment}
        onAddSubtask={addSubtask}
        onPatchSubtask={patchSubtask}
        onAssignSubtask={assignSubtask}
        members={meta.members || []}
        onDeleteSubtask={removeSubtask}
      />
    </ResponsiveLayout>
  );
}
